import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CheckCircle, XCircle, HourglassMedium, ClockCounterClockwise, User, Info, DownloadSimple,
} from "@phosphor-icons/react";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

function fmtDate(iso, lang) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00");
  return d.toLocaleDateString(lang === "en" ? "en-US" : "es-MX",
    { day: "2-digit", month: "short", year: "numeric" });
}

function fmtRelTime(iso, t) {
  if (!iso) return "";
  const dt = new Date(iso);
  const diff = Math.round((Date.now() - dt.getTime()) / (60 * 1000));
  if (diff < 60) return `${t("hrReqAgo")} ${diff} ${t("hrReqMinShort")}`;
  const h = Math.round(diff / 60);
  if (h < 48) return `${t("hrReqAgo")} ${h} ${t("hrReqHourShort")}`;
  const d = Math.round(h / 24);
  return `${t("hrReqAgo")} ${d} ${t("hrReqDayShort")}`;
}

export default function HrRequestsTab({ canManage }) {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("pending");
  const [loading, setLoading] = useState(true);
  const [review, setReview] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const TYPE_LABEL = {
    vacation: t("hrReqTypeVacation"),
    permit: t("hrReqTypePermit"),
    medical: t("hrReqTypeMedical"),
  };
  const STATUS_BADGE = {
    pending:  { l: t("hrReqStatusPending"),  cls: "bg-amber-100 text-amber-800 border-amber-300",     I: HourglassMedium },
    approved: { l: t("hrReqStatusApproved"), cls: "bg-emerald-100 text-emerald-800 border-emerald-300", I: CheckCircle },
    rejected: { l: t("hrReqStatusRejected"), cls: "bg-red-100 text-red-800 border-red-300",             I: XCircle },
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status === "all" ? {} : { status };
      const r = await api.get("/hr/requests", { params });
      setItems(r.data || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const openReview = (req, action) => { setReview({ ...req, action }); setNote(""); };

  const confirmReview = async () => {
    if (!review) return;
    setBusy(true);
    try {
      const url = `/hr/requests/${review.id}/${review.action}`;
      const r = await api.post(url, { note: note.trim() });
      if (review.action === "approve") {
        toast.success(`${t("hrReqApprovedToast")} · ${r.data.days_applied} ${t("hrReqDaysMarked")}`);
      } else {
        toast.success(t("hrReqRejectedToast"));
      }
      window.dispatchEvent(new CustomEvent("hr-requests-updated"));
      const es = r.data.email_status;
      if (es === "enviado") toast.info(t("hrReqEmailSent"));
      else if (es === "sin_email") toast.warning(t("hrReqEmailNoAddress"));
      else if (es === "sin_configurar") toast.warning(t("hrReqEmailNoConfig"));
      setReview(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const exportXlsx = async () => {
    try {
      const params = status === "all" ? {} : { status };
      const r = await api.get("/hr/requests/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      const suffix = status === "all" ? "todas" : status;
      a.download = `solicitudes-${suffix}.xlsx`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(t("hrReqExportedToast"));
    } catch (e) { toast.error(apiError(e)); }
  };

  const pendingCount = items.filter((x) => x.status === "pending").length;

  return (
    <div className="space-y-4 pt-2" data-testid="hr-requests-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-[10px] uppercase tracking-widest font-bold text-slate-500">
            {t("hrReqSubtitle")}
          </p>
          {status === "pending" && (
            <p className="text-xs text-slate-500 mt-0.5">
              {pendingCount === 0 ? t("hrReqNoPending") : `${pendingCount} ${t("hrReqPendingN")}`}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={exportXlsx}
            data-testid="hr-req-export-xlsx"
            className="gap-1.5 text-xs"
            disabled={items.length === 0}
          >
            <DownloadSimple size={13} weight="bold" /> {t("hrReqExportBtn")}
          </Button>
          <Label className="text-[10px] uppercase font-bold text-slate-500">{t("hrReqStatusFilter")}</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 h-8" data-testid="hr-req-filter"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="pending" data-testid="hr-req-filter-pending">{t("hrReqStatusPending")}</SelectItem>
              <SelectItem value="approved" data-testid="hr-req-filter-approved">{t("hrReqStatusApproved")}</SelectItem>
              <SelectItem value="rejected" data-testid="hr-req-filter-rejected">{t("hrReqStatusRejected")}</SelectItem>
              <SelectItem value="all" data-testid="hr-req-filter-all">{t("hrReqStatusAll")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400 text-sm">{t("hrReqLoading")}</div>
      ) : items.length === 0 ? (
        <div className="py-16 text-center border border-dashed border-slate-200 rounded-lg space-y-2" data-testid="hr-req-empty">
          <Info size={32} weight="duotone" className="mx-auto text-slate-300" />
          <p className="text-sm text-slate-500">
            {status === "pending" ? t("hrReqEmptyPending") : t("hrReqEmptyOther")}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="py-2 px-3">{t("hrReqColEmployee")}</th>
                <th className="py-2 px-3">{t("hrReqColType")}</th>
                <th className="py-2 px-3">{t("hrReqColRange")}</th>
                <th className="py-2 px-3">{t("hrReqColReason")}</th>
                <th className="py-2 px-3">{t("hrReqColRequested")}</th>
                <th className="py-2 px-3">{t("hrReqColStatus")}</th>
                {canManage && <th className="py-2 px-3 text-right">{t("hrReqColActions")}</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const badge = STATUS_BADGE[r.status] || STATUS_BADGE.pending;
                const B = badge.I;
                return (
                  <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`hr-req-row-${r.id}`}>
                    <td className="py-2 px-3">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-slate-400" />
                        <div>
                          <p className="font-semibold text-slate-900">{r.employee_name}</p>
                          <p className="text-[11px] text-slate-500">
                            {r.employee_position}{r.employee_crew ? ` · ${r.employee_crew}` : ""}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded ${
                        r.type === "vacation" ? "bg-blue-100 text-blue-800" :
                        r.type === "permit" ? "bg-purple-100 text-purple-800" :
                        "bg-orange-100 text-orange-800"
                      }`}>{TYPE_LABEL[r.type] || r.type}</span>
                    </td>
                    <td className="py-2 px-3 font-mono text-xs text-slate-700">
                      {fmtDate(r.date_from, lang)} → {fmtDate(r.date_to, lang)}
                    </td>
                    <td className="py-2 px-3 text-xs text-slate-600 max-w-[220px] truncate">{r.note || "—"}</td>
                    <td className="py-2 px-3 text-xs text-slate-500">
                      <ClockCounterClockwise size={11} className="inline mr-1" />
                      {fmtRelTime(r.created_at, t)}
                    </td>
                    <td className="py-2 px-3">
                      <span className={`text-[10px] uppercase font-bold px-2 py-1 rounded border inline-flex items-center gap-1 ${badge.cls}`}>
                        <B size={11} weight="bold" /> {badge.l}
                      </span>
                      {r.review_note && (
                        <p className="text-[10px] text-slate-500 italic mt-1 max-w-[180px] truncate">
                          {t("hrReqNotePrefix")}: {r.review_note}
                        </p>
                      )}
                    </td>
                    {canManage && (
                      <td className="py-2 px-3 text-right">
                        {r.status === "pending" ? (
                          <div className="flex gap-1 justify-end">
                            <Button
                              size="sm"
                              onClick={() => openReview(r, "approve")}
                              data-testid={`hr-req-approve-${r.id}`}
                              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 gap-1"
                            >
                              <CheckCircle size={12} weight="bold" /> {t("hrReqBtnApprove")}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openReview(r, "reject")}
                              data-testid={`hr-req-reject-${r.id}`}
                              className="h-7 text-xs text-red-700 border-red-200 hover:bg-red-50 gap-1"
                            >
                              <XCircle size={12} weight="bold" /> {t("hrReqBtnReject")}
                            </Button>
                          </div>
                        ) : (
                          <span className="text-[11px] text-slate-400">
                            {r.reviewed_by ? `${t("hrReqReviewedByPrefix")} ${r.reviewed_by}` : "—"}
                          </span>
                        )}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!review} onOpenChange={(v) => !v && setReview(null)}>
        <DialogContent className="max-w-md" data-testid="hr-req-review-dialog">
          <DialogHeader>
            <DialogTitle>
              {review?.action === "approve" ? t("hrReqApproveDialogTitle") : t("hrReqRejectDialogTitle")}
            </DialogTitle>
          </DialogHeader>
          {review && (
            <div className="space-y-3 text-sm">
              <p><span className="text-slate-500">{t("hrReqColEmployee")}:</span> <b>{review.employee_name}</b></p>
              <p><span className="text-slate-500">{t("hrReqColType")}:</span> <b>{TYPE_LABEL[review.type]}</b></p>
              <p><span className="text-slate-500">{t("hrReqColRange")}:</span> <b>{fmtDate(review.date_from, lang)} → {fmtDate(review.date_to, lang)}</b></p>
              {review.note && <p><span className="text-slate-500">{t("hrReqColReason")}:</span> <i>{review.note}</i></p>}
              <div>
                <Label className="text-xs">{t("hrReqReviewNote")}</Label>
                <Input
                  data-testid="hr-req-review-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder={review.action === "approve" ? t("hrReqPlaceholderApprove") : t("hrReqPlaceholderReject")}
                  className="mt-1"
                  maxLength={400}
                />
              </div>
              {review.action === "approve" && (
                <p className="text-xs text-slate-500 bg-emerald-50 border border-emerald-200 rounded p-2">
                  ✓ {t("hrReqAutoDaysInfo", { type: TYPE_LABEL[review.type] })}
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReview(null)} disabled={busy}>{t("hrReqCancel")}</Button>
            <Button
              onClick={confirmReview}
              disabled={busy}
              data-testid="hr-req-review-confirm"
              className={review?.action === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
            >
              {busy ? t("hrReqProcessing") : (review?.action === "approve" ? t("hrReqBtnApprove") : t("hrReqBtnReject"))}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
